import React, { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { PlatformTypeDto, PlatformTypeId } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { ServerType } from 'api/Trading-Accounts/Types/ServerType'
import {
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Link } from 'components/Link'
import { Spinner } from 'components/Spinner'
import { infoToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useGridState } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchPlatformTypesAction } from 'store/trading-accounts/trading-accounts.actions'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { sleep } from 'utils/sleep'

import {
  ReverseTransactionEquityFormValues,
  TransactionNegativeEquityForm,
} from './TransactionNegativeEquityForm'

export const TransactionNegativeEquityPage = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const setToast = useContext(ToastContext)

  const dispatch = useAppDispatch()

  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)
  const [platformServers, setPlatformServers] = useState<ServerType[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [platform, setPlatformType] = useState<PlatformTypeDto | undefined>(
    platformTypes.length ? platformTypes[0] : undefined
  )
  const [server, setServer] = useState<ServerType | undefined>(undefined)

  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountDto[]>([])
  const { gridState, gridActions } = useGridState()

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    async (query?: PageQuery) => {
      if (!server || !platform) return
      setIsLoading(true)
      try {
        return await tradingAccountsApiClient.getNegativeEquityByServerIdAndPlatformType({
          ...query,
          pageSize: gridState.take,
          pageIndex: gridState.pageIndex,
          serverId: server.id,
          platformTypeId: platform.id,
        })
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    },
    [server?.id, platform?.id, gridState.take, gridState.pageIndex]
  )
  const { data } = useFetchOne(fetchData)

  useEffect(() => {
    if (isEmpty(platformTypes)) {
      dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))
    }
  }, [platformTypes, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchServerActionAndSetDefault = async (platformId: PlatformTypeId) => {
    try {
      setIsLoading(true)
      setServer(undefined)
      setPlatformServers([])
      const resp = await tradingAccountsApiClient.getTradingAccountServerType(platformId)
      setPlatformServers(resp)
      if (resp.length) {
        setServer(resp[0])
      }
    } catch (e: unknown) {
      console.error(e)
      setServer(undefined)
      setPlatformServers([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (data) {
      setTradingAccounts(data.items.map((x) => ({ ...x, selected: false })) as TradingAccountDto[])
      gridActions.setGridFilters({ total: data.totalResults })
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })

    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())

    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()

    gridActions.setGridFilters({
      take,
      skip,
      pageIndex,
    })
  }

  const selectionChange = (event: GridSelectionChangeEvent) => {
    const results = tradingAccounts.map((item) => {
      if (item.id === event.dataItem.id) {
        item.selected = !event.dataItem.selected
      }
      return item
    })
    setTradingAccounts(results)
  }

  const getSelectedTA = () => {
    return tradingAccounts.filter((taDto) => taDto.selected)
  }

  const onSubmit = async (values: ReverseTransactionEquityFormValues) => {
    const selected = getSelectedTA()
    if (selected.length === 0) {
      return setToast(
        infoToast('You must select at least one trading account to create a transaction.')
      )
    }
    await Promise.all(
      selected.map(async (tradingAccount) => {
        await walletsApiClient.createRefundTransferToTradingAccount({
          tradingAccountId: tradingAccount.id,
          directionTypeId: 200,
          transactionTypeId: 710,
          amount: Math.abs(tradingAccount.platformOverview.equity),
          comment: values.comment,
        })
      })
    )
    setToast(successToast('Refund for account succeeded. Wait for refresh the data.'))
    await sleep(2500)
    forceRefresh()
  }

  if (isLoading) return <Spinner />

  return (
    <React.Fragment>
      <FormBaseFieldBlock>
        <FormBaseRow style={{ width: 300 }}>
          <FormBaseFieldLabel>Platform</FormBaseFieldLabel>
          <FormBaseFieldGroup>
            <select
              value={platform ? platform.id : ''}
              autoComplete='off'
              onChange={(e) => {
                const selectedPlatform = (platformTypes || []).find(
                  (pl) => pl.id === Number(e.target.value)
                )
                if (selectedPlatform) {
                  setPlatformType(selectedPlatform)
                  setServer(undefined)
                  fetchServerActionAndSetDefault(selectedPlatform.id)
                }
              }}
            >
              <option key={0} value='' disabled>
                Select Platform
              </option>
              {platformTypes.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </FormBaseFieldGroup>
        </FormBaseRow>
      </FormBaseFieldBlock>
      <FormBaseFieldBlock>
        <FormBaseRow style={{ width: 300 }}>
          <FormBaseFieldLabel>Server</FormBaseFieldLabel>
          <FormBaseFieldGroup>
            <select
              value={server ? server.id : ''}
              autoComplete='off'
              onChange={(e) => {
                const serverById = (platformServers || []).find((srv) => srv.id === e.target.value)
                if (serverById) {
                  setServer(serverById)
                }
              }}
            >
              <option key={0} value='' disabled>
                Select Server
              </option>
              {platformServers.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </FormBaseFieldGroup>
        </FormBaseRow>
      </FormBaseFieldBlock>

      <Grid
        style={{ zIndex: 0 }}
        resizable
        sortable
        take={gridState.take}
        skip={gridState.skip}
        total={gridState.total}
        data={tradingAccounts}
        selectedField='selected'
        onSelectionChange={selectionChange}
        pageable={pageableSettings}
        onPageChange={handlePageChange}
        data-test='table'
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={tradingAccounts.findIndex((dataItem) => !dataItem.selected) === -1}
        />
        <Column
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
              <Link
                to={{
                  pathname: generatePath(links.recordsClients.detailsUrl as string, {
                    id: gridCellProps.dataItem.client.id,
                    slug: 'details',
                  }),
                }}
              >
                {gridCellProps.dataItem.client.name}
              </Link>
            </td>
          )}
          title='Client'
        />
        <Column
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
              <Link
                to={{
                  pathname: generatePath(links.recordsAccountGroups.detailsUrl as string, {
                    id: gridCellProps.dataItem.accountGroup.id,
                    slug: 'details',
                  }),
                }}
              >
                {gridCellProps.dataItem.accountGroup.name}
              </Link>
            </td>
          )}
          title='Account Group'
        />
        <Column
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
              <Link
                to={{
                  pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                    id: gridCellProps.dataItem.wallet.id,
                    slug: 'details',
                  }),
                }}
              >
                {gridCellProps.dataItem.wallet.name}
              </Link>
            </td>
          )}
          title='Wallet'
        />
        <Column
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
              <Link
                to={{
                  pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                    id: gridCellProps.dataItem.id,
                    slug: 'details',
                  }),
                }}
              >
                {gridCellProps.dataItem.platformOverview.login}
              </Link>
            </td>
          )}
          title='Trading Account'
        />
        <Column className='table-columns__content' field='accountType.name' title='Account Type' />
        <Column className='table-columns__content' field='status.name' title='Status' />
        <Column className='table-columns__content' field='platformOverview.equity' title='Equity' />
        <Column
          className='table-columns__content'
          field='platformOverview.balance'
          title='Balance'
        />
        <Column
          className='table-columns__content'
          field='platformOverview.usedMargin'
          title='Used Margin'
        />
        <Column
          title='Created Date'
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__date'>
              {formatLocalDateUTC(gridCellProps.dataItem.createdDate, true)}
            </td>
          )}
        />
        <Column
          title='Updated Date'
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__date'>
              {formatLocalDateUTC(gridCellProps.dataItem.updatedDate, true)}
            </td>
          )}
        />
      </Grid>
      <TransactionNegativeEquityForm
        selectedTradingAccounts={getSelectedTA()}
        onSubmit={onSubmit}
      />
    </React.Fragment>
  )
}
